import { useState } from 'react';
import {
  BulkRetryExecuteResponse,
  BulkRetryFilter,
  BulkRetryPreviewResponse,
} from '../../../interfaces/bulkReprocess';
import referralService from '../../../services/referral';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

export const useBulkReprocess = () => {
  const [preview, setPreview] = useState<BulkRetryPreviewResponse | null>(null);
  const [executeResult, setExecuteResult] = useState<BulkRetryExecuteResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const runPreview = async (filter: BulkRetryFilter) => {
    setLoadingPreview(true);
    setPreview(null);
    setExecuteResult(null);
    try {
      const response = await referralService.bulkRetryPreview(filter);
      setPreview(response);
      if (response.count === 0) {
        setSnackbar({
          open: true,
          message: 'No documents matched the filter.',
          severity: 'info',
        });
      } else if (response.count > response.maxBatchSize) {
        setSnackbar({
          open: true,
          message: `Matched ${response.count} documents — exceeds the ${response.maxBatchSize} batch cap. Narrow the filter.`,
          severity: 'warning',
        });
      }
    } catch (error) {
      console.error('bulkRetryPreview failed', error);
      setSnackbar({
        open: true,
        message: 'Preview failed. Check the console / network tab.',
        severity: 'error',
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const runExecute = async (params: { reason: string; spacingMs?: number }) => {
    if (!preview || preview.totalIds.length === 0) return;

    setLoadingExecute(true);
    try {
      const response = await referralService.bulkRetryExecute({
        documentIds: preview.totalIds,
        reason: params.reason,
        spacingMs: params.spacingMs,
      });
      setExecuteResult(response);
      setSnackbar({
        open: true,
        message: `Batch ${response.batchId} enqueued ${response.enqueuedCount} docs (failed=${response.failedIds.length}).`,
        severity: 'success',
      });
    } catch (error) {
      console.error('bulkRetryExecute failed', error);
      setSnackbar({
        open: true,
        message: 'Execute failed. Check the console / network tab.',
        severity: 'error',
      });
    } finally {
      setLoadingExecute(false);
    }
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return {
    preview,
    executeResult,
    loadingPreview,
    loadingExecute,
    snackbar,
    runPreview,
    runExecute,
    closeSnackbar,
  };
};
